import React, {useState, useContext, useEffect, useMemo} from "react"
import {useParams} from "react-router-dom"

import CenterLoader from "../../components/CenterLoader"
import {Main} from "../../components/Layout"
import RefContext from "../../utilities/RefContext"
import SubHeader from "../../components/SubHeader"
import {wrappedGet, wrappedPut, wrappedPost} from "../../utilities/AxiosHelper"
import {SkeletonRows} from "../../utilities/general.jsx"
import {useStableNavigate} from "../../utilities/StableNavigateContext"

/**
 * Unit Component
 * Renders the Unit view
 */
const Unit = () => {
    const {organizationId, facilityId, unitId} = useParams()
    const {dispatch} = useContext(RefContext)
    const navigate = useStableNavigate()

    const [unit, setUnit] = useState({
        name: "",
        shortname: "",
        active: true,
        customerKey: "",
        level_of_care: "",
        shift_length_hours: 0,
        shifts_per_week: 0,
        num_onboarding_weeks: 0,
    })
    const [levelsOfCare, setLevelsOfCare] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [userTypes, setUserTypes] = useState([])
    // to set the experience level
    const [experienceLevels, setExperienceLevels] = useState([])
    const [avgOrientationLengths, setAvgOrientationLengths] = useState([])
    const [avgOrientationLength, setAvgOrientationLength] = useState(0)
    const [experienceLevel, setExperienceLevel] = useState("")
    const [typeId, setTypeId] = useState("")
    const [aolId, setAolId] = useState(null)
    const [saveStatus, setSaveStatus] = useState("create")

    useEffect(() => {
        fetchAll()
    }, [])

    useEffect(() => {
        if (unit.name) {
            document.title = `Unit - ${unit.name}`
        }
    }, [unit.name])

    const fetchAll = () => {
        setIsLoading(true)
        Promise.all([
            fetchUnitData(),
            fetchLevelOfCareData(),
            fetchExperienceLevels(),
            fetchUserTypes(),
            fetchAvgOrientationLengths(),
        ])
            .catch(() => {
                // optional: global error handling if you want
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const fetchAvgOrientationLengths = () => {
        return wrappedGet(`avg-orientation-length/${unitId}`)
            .then((response) => {
                if (response.data && response.data.results.length > 0) {
                    setAvgOrientationLengths(response.data.results)
                }
            })
            .catch((error) => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem fetching Unit Orientation Lengths",
                    timeout: 5000,
                })
            })
    }

    const fetchUserTypes = () => {
        return wrappedGet("get-types")
            .then((response) => {
                if (response.data && response.data.results.length > 0) {
                    setUserTypes(response.data.results)
                }
            })
            .catch((error) => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem fetching User Types",
                    timeout: 5000,
                })
            })
    }

    const fetchExperienceLevels = () => {
        wrappedGet(`experience-levels`)
            .then((response) => {
                const data = response.data.results
                if (!data) return

                setExperienceLevels(data)
            })
            .catch(() => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem fetching Experience Levels",
                    timeout: 5000,
                })
            })
    }

    const fetchUnitData = () => {
        wrappedGet(`organization/${organizationId}/facility/${facilityId}/unit/${unitId}`)
            .then((response) => {
                const data = response.data

                if (!data) return

                let customerKey = ""
                if (data.customer_key !== undefined && data.customer_key !== null) {
                    customerKey = data.customer_key
                }

                setUnit({
                    name: data.name,
                    shortname: data.shortname,
                    active: data.active,
                    customerKey,
                    level_of_care: data.level_of_care || "",
                    shift_length_hours: data.shift_length_hours,
                    shifts_per_week: data.shifts_per_week || 0,
                    num_onboarding_weeks: data.num_onboarding_weeks || 0,
                    precision_staffing: data.precision_staffing ?? false,
                })
            })
            .catch(() => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem fetching Unit",
                    timeout: 5000,
                })
            })
    }

    const fetchLevelOfCareData = () => {
        wrappedGet(`levelofcare?organization_id=${organizationId}`)
            .then((response) => {
                const data = response.data

                if (!data) return

                setLevelsOfCare(data.results)
            })
            .catch(() => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem fetching Unit - Levels of Care",
                    timeout: 5000,
                })
            })
    }

    const handleSave = () => {
        setIsLoading(true)
        wrappedPut(`organization/${organizationId}/facility/${facilityId}/unit/${unitId}`, unit)
            .then(() => {
                dispatch("showToast", {
                    show: true,
                    variant: "success",
                    text: "Unit Updated",
                    timeout: 5000,
                })
            })
            .catch(() => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem Saving Facility Unit",
                    timeout: 5000,
                })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const saveAvgOrientationLength = () => {
        setIsLoading(true)
        let call = null
        if (saveStatus === "create") {
            call = wrappedPost(`avg-orientation-length`, {
                unit_id: unitId,
                exp_level: experienceLevel,
                type_id: typeId,
                orientation_length: avgOrientationLength,
            })
        } else if (saveStatus === "update") {
            call = wrappedPut(`avg-orientation-length/${aolId}`, {
                unit_id: unitId,
                exp_level: experienceLevel,
                type_id: typeId,
                orientation_length: avgOrientationLength,
            })
        }
        call.then((response) => {
            setAolId(null)
            setAvgOrientationLength(0)
            setExperienceLevel("")
            setTypeId("")
            setSaveStatus("create")
            fetchAvgOrientationLengths()
        })
            .catch(() => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem fetching Average Orientation Lengths",
                    timeout: 5000,
                })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const lookupExperienceLevelName = (id) => {
        const expLevel = experienceLevels.find((f) => f.id === id)
        return expLevel?.name || "Unknown"
    }

    const lookupUserTypeName = (id) => {
        const userType = userTypes.find((f) => f.id === id)
        return userType?.name || "Unknown"
    }

    const editLength = (length) => {
        setSaveStatus("update")
        setAvgOrientationLength(length.val)
        setExperienceLevel(length.user_type_experience_level_id)
        setTypeId(length.type_id)
        setAolId(length.id)
    }

    const userExperienceMap = useMemo(() => {
        const map = {}
        userTypes.forEach((userType) => {
            experienceLevels.forEach((expLevel) => {
                const key = `${userType.id}_${expLevel.id}`
                map[key] = `${userType.name} - ${expLevel.name}`
            })
        })
        return map
    }, [userTypes, experienceLevels])

    // Existing length rows keyed the same way as userExperienceMap, so a combo's
    // label (userExperienceMap[key]) and its saved row (existingLengthsByCombo[key]) align.
    const existingLengthsByCombo = useMemo(() => {
        const map = {}
        avgOrientationLengths.forEach((aol) => {
            map[`${aol.type_id}_${aol.user_type_experience_level_id}`] = aol
        })
        return map
    }, [avgOrientationLengths])

    const currentComboKey = typeId && experienceLevel ? `${typeId}_${experienceLevel}` : ""
    const isDuplicateCombo =
        saveStatus === "create" && !!currentComboKey && currentComboKey in existingLengthsByCombo

    const handleBack = () => {
        navigate(`/admin/organization/${organizationId}/facility/${facilityId}`)
    }

    if (isLoading) {
        return <CenterLoader />
    }

    return (
        <>
            <SubHeader title1="Units" title2="" icon="fa-users-cog">
                <div className="d-flex flex-grow-1 justify-content-end">
                    <button className="btn btn-secondary fw-semibold me-2 px-6" onClick={handleBack}>
                        <i className="fas fa-arrow-left"></i>Back
                    </button>
                </div>
            </SubHeader>

            <Main>
                <div className="content d-flex flex-column flex-column-fluid">
                    <div className="container-fluid">
                        <div className="py-3 py-lg-6">
                            <div className="card">
                                <div className="col-xxl-12">
                                    <div className="row g-8 p-8 d-flex flex-row align-items-end">
                                        <div className="col">
                                            <div className="row g-8 align-items-end">
                                                <div className="col-4">
                                                    <label
                                                        htmlFor="id-unit-name"
                                                        className="form-label fw-bold required"
                                                    >
                                                        Name:
                                                    </label>
                                                    <input
                                                        id="id-unit-name"
                                                        type="text"
                                                        className="form-control form-control-solid ps-12 required"
                                                        placeholder="Name"
                                                        value={unit.name}
                                                        onChange={(evt) => {
                                                            setUnit((prev) => ({
                                                                ...prev,
                                                                name: evt.target.value,
                                                            }))
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-3">
                                                    <label
                                                        htmlFor="id-unit-shortname"
                                                        className="form-label fw-bold required"
                                                    >
                                                        Short Name:
                                                    </label>
                                                    <input
                                                        id="id-unit-shortname"
                                                        type="text"
                                                        className="form-control form-control-solid ps-12 required"
                                                        placeholder="Short Name"
                                                        value={unit.shortname}
                                                        onChange={(evt) => {
                                                            setUnit((prev) => ({
                                                                ...prev,
                                                                shortname: evt.target.value,
                                                            }))
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-3">
                                                    <label
                                                        htmlFor="id-unit-customer-key"
                                                        className="form-label fw-bold"
                                                    >
                                                        Customer Key:
                                                    </label>
                                                    <input
                                                        id="id-unit-customer-key"
                                                        type="text"
                                                        className="form-control form-control-solid ps-12"
                                                        placeholder="Key"
                                                        value={unit.customerKey}
                                                        onChange={(evt) => {
                                                            setUnit((prev) => ({
                                                                ...prev,
                                                                customerKey: evt.target.value,
                                                            }))
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-2">
                                                    <label
                                                        htmlFor="id-unit-shift-length-hours"
                                                        className="form-label fw-bold"
                                                    >
                                                        Shift Length Hours:
                                                    </label>
                                                    <input
                                                        id="id-unit-shift-length-hours"
                                                        type="number"
                                                        min={0}
                                                        className="form-control form-control-solid ps-12"
                                                        placeholder="Hours"
                                                        value={unit.shift_length_hours}
                                                        onChange={(evt) => {
                                                            setUnit((prev) => ({
                                                                ...prev,
                                                                shift_length_hours: evt.target.value,
                                                            }))
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-2">
                                                    <label
                                                        htmlFor="id-unit-shifts-per-week"
                                                        className="form-label fw-bold"
                                                    >
                                                        Shifts per Week:
                                                    </label>
                                                    <input
                                                        id="id-unit-shifts-per-week"
                                                        type="number"
                                                        min={0}
                                                        max={7}
                                                        className="form-control form-control-solid ps-12"
                                                        placeholder="Shifts"
                                                        value={unit.shifts_per_week}
                                                        onChange={(evt) => {
                                                            setUnit((prev) => ({
                                                                ...prev,
                                                                shifts_per_week: evt.target.value,
                                                            }))
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-2">
                                                    <label
                                                        htmlFor="id-unit-number-onboarding-weeks"
                                                        className="form-label fw-bold"
                                                    >
                                                        Number of Onboarding Weeks:
                                                    </label>
                                                    <input
                                                        id="id-unit-number-onboarding-weeks"
                                                        type="number"
                                                        min={0}
                                                        className="form-control form-control-solid ps-12"
                                                        placeholder="Number"
                                                        value={unit.num_onboarding_weeks}
                                                        onChange={(evt) => {
                                                            setUnit((prev) => ({
                                                                ...prev,
                                                                num_onboarding_weeks: evt.target.value,
                                                            }))
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-2">
                                                    <label htmlFor="id-unit-active" className="form-label fw-bold">
                                                        Active:
                                                    </label>
                                                    <select
                                                        id="id-unit-active"
                                                        className="form-select form-select-solid fw-bold"
                                                        value={unit.active ? "true" : "false"}
                                                        onChange={(evt) => {
                                                            setUnit((prev) => ({
                                                                ...prev,
                                                                active: evt.target.value === "true",
                                                            }))
                                                        }}
                                                    >
                                                        <option value="true">Yes</option>
                                                        <option value="false">No</option>
                                                    </select>
                                                </div>
                                                <div className="col-2">
                                                    <label
                                                        htmlFor="id-unit-leve-of-care"
                                                        className="form-label fw-bold"
                                                    >
                                                        Level of Care:
                                                    </label>
                                                    <select
                                                        id="id-unit-leve-of-care"
                                                        className="form-select form-select-solid fw-bold"
                                                        value={unit.level_of_care}
                                                        onChange={(evt) => {
                                                            setUnit((prev) => ({
                                                                ...prev,
                                                                level_of_care: evt.target.value,
                                                            }))
                                                        }}
                                                    >
                                                        {levelsOfCare.map((item) => (
                                                            <option key={item.id} value={item.id}>
                                                                {item.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-2">
                                                    <label htmlFor="id-unit-active" className="form-label fw-bold">
                                                        Precision Staffing:
                                                    </label>
                                                    <select
                                                        id="id-unit-active"
                                                        className="form-select form-select-solid fw-bold"
                                                        value={unit.precision_staffing ? "true" : "false"}
                                                        onChange={(evt) => {
                                                            setUnit((prev) => ({
                                                                ...prev,
                                                                precision_staffing: evt.target.value === "true",
                                                            }))
                                                        }}
                                                    >
                                                        <option value="true">Yes</option>
                                                        <option value="false">No</option>
                                                    </select>
                                                </div>

                                                <div className="col-lg-2 col-md-6">
                                                    <div className="d-flex justify-content-start">
                                                        <button
                                                            type="submit"
                                                            className="btn btn-secondary fw-semibold me-2 px-6"
                                                            onClick={handleSave}
                                                        >
                                                            Save
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="py-3 py-lg-6">
                            <div className="card card-xl-stretch mb-xl-8">
                                <div className="card-body pt-0">
                                    <div className="card-header border-0 p-5">
                                        <h3 className="card-title align-items-start flex-column">
                                            <span className="card-label fw-bold fs-2 text-gray-900">
                                                Average Orientation Lengths
                                            </span>
                                        </h3>
                                        {isDuplicateCombo && (
                                            <div className="text-danger fs-5 mt-1 px-4">
                                                {userExperienceMap[currentComboKey]} already has a length.
                                            </div>
                                        )}
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table align-middle table-rounded table-row-bordered border gs-4 my-0 fs-6 w-100">
                                            <thead className="text-start text-light text-uppercase bg-gray-700 align-middle fs-7 fw-bold">
                                                <tr>
                                                    <td>User Type</td>
                                                    <td>Experience Level</td>
                                                    <td>Length</td>
                                                    <td></td>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>
                                                        <select
                                                            id="id-unit-user-type"
                                                            className="form-select form-select-solid fw-bold"
                                                            value={typeId}
                                                            onChange={(evt) => {
                                                                const val = evt.target.value || ""
                                                                setTypeId(val)
                                                                setExperienceLevel("")
                                                                if (!val) setAvgOrientationLength(0)
                                                            }}
                                                        >
                                                            <option key="user-type-none" value="">
                                                                Select
                                                            </option>
                                                            {userTypes.map((type) => (
                                                                <option key={`user-type-${type.id}`} value={type.id}>
                                                                    {type.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <select
                                                            id="id-unit-exp-level"
                                                            className="form-select form-select-solid fw-bold"
                                                            value={experienceLevel}
                                                            disabled={!typeId}
                                                            onChange={(evt) => {
                                                                setExperienceLevel(evt.target.value)
                                                            }}
                                                        >
                                                            <option key="exp-level-none" value="">{typeId ? "Select" : "Select User Type first"}</option>
                                                            {experienceLevels.map((exp) => (
                                                                <option key={`exp-level-${exp.id}`} value={exp.id}>
                                                                    {exp.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <input
                                                            id="id-avg-orientation-length"
                                                            type="number"
                                                            min="0"
                                                            className="form-control form-control-solid ps-12 required"
                                                            placeholder="Orientation Length"
                                                            value={avgOrientationLength}
                                                            disabled={!typeId}
                                                            onChange={(evt) => {
                                                                setAvgOrientationLength(evt.target.value)
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <button
                                                            type="submit"
                                                            className="btn btn-secondary fw-semibold me-2 px-6"
                                                            onClick={saveAvgOrientationLength}
                                                            disabled={!typeId || !experienceLevel || isDuplicateCombo}
                                                        >
                                                            {saveStatus === "create" ? "Save" : "Update"}
                                                        </button>
                                                    </td>
                                                </tr>
                                                {isLoading ? (
                                                    <SkeletonRows colCount={4} rowCount={4} />
                                                ) : (
                                                    avgOrientationLengths.map((aol) => (
                                                        <tr key={`avg-orientation-list-${aol.id}`}>
                                                            <td>{lookupUserTypeName(aol.type_id)}</td>
                                                            <td>
                                                                {lookupExperienceLevelName(
                                                                    aol.user_type_experience_level_id
                                                                )}
                                                            </td>
                                                            <td>{aol.val}</td>
                                                            <td>
                                                                <button
                                                                    className="btn btn-icon btn-light btn-active-light-primary w-30px h-30px w-md-40px h-md-40px hover-elevate-down position-relative"
                                                                    onClick={() => editLength(aol)}
                                                                >
                                                                    <i className="fas fa-edit text-primary fs-3"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Main>
        </>
    )
}

export default Unit
